import React, { useState } from "react";
import { Paper, Typography, Divider, Button, IconButton, Box } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort } from '@syncfusion/ej2-react-grids';
import { AddCenterDialog } from '../dialogs/AddCenterDialog';

export interface Center {
    code: string;
    name: string;
    accessUntil?: string | null;
}

interface CenterAccessGridProps {
    centerAccess: Center[];
    availableCenters: Center[];
    onAdd?: (center: Center) => void;
    onDelete?: (center: Center) => void;
}

export const CenterAccessGrid: React.FC<CenterAccessGridProps> = ({ centerAccess, availableCenters, onAdd, onDelete }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCenter, setSelectedCenter] = useState<Center | null>(null);

    // Filter out centers already in centerAccess
    const centersToAdd = availableCenters.filter(
        (center) => !centerAccess.some((c) => c.code === center.code)
    );

    const handleAddClick = () => setDialogOpen(true);
    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedCenter(null);
    };
    const handleCenterSelect = (center: Center) => setSelectedCenter(center);
    const handleAddConfirm = () => {
        if (selectedCenter && onAdd) {
            onAdd(selectedCenter);
            handleDialogClose();
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mt: 3, minWidth: 600 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" gutterBottom>Center Access</Typography>
                {onAdd && (
                    <Button variant="outlined" startIcon={<Add />} onClick={handleAddClick} size="small">
                        Add
                    </Button>
                )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <GridComponent
                dataSource={centerAccess}
                allowPaging={false}
                allowSorting={true}
                rowHeight={32}
                allowSelection={false}
                height={360}
            >
                <ColumnsDirective>
                    <ColumnDirective field="name" headerText="Name" width={200} />
                    <ColumnDirective field="code" headerText="Code" width={120} />
                    {onDelete && (
                        <ColumnDirective
                            headerText="Actions"
                            width={100}
                            template={(props: Center) => (
                                <IconButton color="error" onClick={() => onDelete(props)}>
                                    <Delete />
                                </IconButton>
                            )}
                            textAlign="Center"
                        />
                    )}
                </ColumnsDirective>
                <Inject services={[Sort]} />
            </GridComponent>
            <AddCenterDialog
                open={dialogOpen}
                centersToAdd={centersToAdd}
                selectedCenter={selectedCenter}
                onSelect={handleCenterSelect}
                onClose={handleDialogClose}
                onAdd={handleAddConfirm}
            />
        </Paper>
    );
};